import { type MaritalStatus } from '../customer/customer'

export interface Disclosure {
  __typename?: 'Disclosure'
  accepted: boolean
  id: number
  recordedTimestamp?: string
  version?: string
}

export interface DisclosureInput {
  accepted: boolean
  id: number
}

export interface DisclosuresInput {
  customerId: number
}

export enum AnnualPersonalIncome {
  Below_50K = 'BELOW_50K',
  Between_50KAnd_100K = 'BETWEEN_50K_AND_100K',
  Between_100KAnd_250K = 'BETWEEN_100K_AND_250K',
  Over_250K = 'OVER_250K',
}

export enum ApplicationStatus {
  Abandoned = 'ABANDONED',
  Approved = 'APPROVED',
  Closed = 'CLOSED',
  ClosedDueToDurationExceedingMaximum = 'CLOSED_DUE_TO_DURATION_EXCEEDING_MAXIMUM',
  ClosedDueToLackOfResponse = 'CLOSED_DUE_TO_LACK_OF_RESPONSE',
  ClosedDueToNewApplication = 'CLOSED_DUE_TO_NEW_APPLICATION',
  ClosedDueToRelatedApplicationInManualReview = 'CLOSED_DUE_TO_RELATED_APPLICATION_IN_MANUAL_REVIEW',
  ClosedDueToUserAlreadyExists = 'CLOSED_DUE_TO_USER_ALREADY_EXISTS',
  Initializing = 'INITIALIZING',
  InProgress = 'IN_PROGRESS',
  ManualReview = 'MANUAL_REVIEW',
  New = 'NEW',
  Rejected = 'REJECTED',
}

export interface DeviceInfoOutput {
  __typename?: 'DeviceInfoOutput'
  deviceId?: string
  isFirstTimeSeenByEvergreen?: boolean
  requestId?: string
  visitorData?: string
}

export enum DocumentVerificationStatus {
  Complete = 'COMPLETE',
  NotStarted = 'NOT_STARTED',
  Pending = 'PENDING',
}

export enum EmploymentStatus {
  Employed = 'EMPLOYED',
  Independent = 'INDEPENDENT',
  Retired = 'RETIRED',
  Student = 'STUDENT',
  Unemployed = 'UNEMPLOYED',
}

export interface EnrollmentBrokerageAccountInput {
  accountNickname?: string
}

export interface EnrollmentInvestmentAccountInput {
  brokerage?: EnrollmentBrokerageAccountInput
}

export interface EquitySectorAllocationInput {
  equitySector?: EquitiesSector
  allocationPercent?: number
}

export interface EquitySectorAllocations {
  consumer?: number
  energy?: number
  financial?: number
  healthcare?: number
  industry?: number
  media?: number
  realEstate?: number
  technology?: number
}

export interface FixedIncomeSectorAllocations {
  corporateBonds?: number
  governmentBonds?: number
  highYieldBonds?: number
  internationalBonds?: number
  municipalBonds?: number
}

export interface FixedIncomeSectorAllocationInput {
  fixedIncomeCategory?: FixedIncomeCategory
  allocationPercent?: number
}

export interface EnrollmentBrokerageAccountOutput {
  __typename?: 'EnrollmentBrokerageAccountOutput'
  accountNickname?: string
}

export interface EnrollmentInvestmentAccountOutput {
  __typename?: 'EnrollmentInvestmentAccountOutput'
  brokerage?: EnrollmentBrokerageAccountOutput
}

export interface EnrollmentBrokerageAccountRecommendationOutput {
  __typename?: 'EnrollmentBrokerageAccountRecommendationOutput'
  accountNickname?: string
}

export interface EnrollmentInvestmentAccountRecommendationOutput {
  __typename?: 'EnrollmentInvestmentAccountRecommendationOutput'
  brokerage?: EnrollmentBrokerageAccountRecommendationOutput
}

export interface EnrollmentInvestmentDisclosureMetadataOutput {
  __typename?: 'EnrollmentInvestmentDisclosureMetadataOutput'
  accepted: boolean
}

export interface EnrollmentInvestmentDisclosureOutput {
  __typename?: 'EnrollmentInvestmentDisclosureOutput'
  customerAccountAgreement?: EnrollmentInvestmentDisclosureMetadataOutput
  fpslDisclosure?: EnrollmentInvestmentDisclosureMetadataOutput
  fpslMasterSecuritiesLendingAgreement?: EnrollmentInvestmentDisclosureMetadataOutput
  marginAgreement?: EnrollmentInvestmentDisclosureMetadataOutput
  privacyPolicy?: EnrollmentInvestmentDisclosureMetadataOutput
  rule14b?: EnrollmentInvestmentDisclosureMetadataOutput
  termsOfUse?: EnrollmentInvestmentDisclosureMetadataOutput
}

export enum EnrollmentInvestmentExperienceCategory {
  ALot = 'A_LOT',
  Moderate = 'MODERATE',
  None = 'NONE',
  Some = 'SOME',
  VeryLittle = 'VERY_LITTLE',
}

export enum EnrollmentInvestmentObjectiveCategory {
  BuildWealth = 'BUILD_WEALTH',
  CashManagement = 'CASH_MANAGEMENT',
  GenerateIncome = 'GENERATE_INCOME',
  SaveForRetirement = 'SAVE_FOR_RETIREMENT',
  SpecificGoal = 'SPECIFIC_GOAL',
}

export enum InvestmentRiskToleranceCategory {
  Aggressive_1 = 'AGGRESSIVE_1',
  Aggressive_2 = 'AGGRESSIVE_2',
  Aggressive_3 = 'AGGRESSIVE_3',
  Aggressive_4 = 'AGGRESSIVE_4',
  Aggressive_5 = 'AGGRESSIVE_5',
  Balanced_1 = 'BALANCED_1',
  Balanced_2 = 'BALANCED_2',
  Balanced_3 = 'BALANCED_3',
  Balanced_4 = 'BALANCED_4',
  Balanced_5 = 'BALANCED_5',
  Conservative_1 = 'CONSERVATIVE_1',
  Conservative_2 = 'CONSERVATIVE_2',
  Conservative_3 = 'CONSERVATIVE_3',
  Conservative_4 = 'CONSERVATIVE_4',
  Conservative_5 = 'CONSERVATIVE_5',
  Growth_1 = 'GROWTH_1',
  Growth_2 = 'GROWTH_2',
  Growth_3 = 'GROWTH_3',
  Growth_4 = 'GROWTH_4',
  Growth_5 = 'GROWTH_5',
  Moderate_1 = 'MODERATE_1',
  Moderate_2 = 'MODERATE_2',
  Moderate_3 = 'MODERATE_3',
  Moderate_4 = 'MODERATE_4',
  Moderate_5 = 'MODERATE_5',
}

export enum EnrollmentTaxFilingStatusType {
  HeadOfHousehold = 'HEAD_OF_HOUSEHOLD',
  MarriedFilingJointly = 'MARRIED_FILING_JOINTLY',
  MarriedFilingSeparately = 'MARRIED_FILING_SEPARATELY',
  Single = 'SINGLE',
}

export interface EnrollmentInvestmentProfileOutput {
  __typename?: 'EnrollmentInvestmentProfileOutput'
  account?: EnrollmentInvestmentAccountOutput
  disclosure?: EnrollmentInvestmentDisclosureOutput
  experience?: EnrollmentInvestmentExperienceCategory
  fpslParticipation?: boolean
  objective?: EnrollmentInvestmentObjectiveCategory
  riskTolerance?: InvestmentRiskToleranceCategory
  taxFilingStatus?: EnrollmentTaxFilingStatusType
}

export enum EnrollmentExperimentId {
  Atequila = 'ATEQUILA',
  Bulleit = 'BULLEIT',
  CsV1 = 'CS_V1',
  FourRoses = 'FOUR_ROSES',
  Jimbeam = 'JIMBEAM',
  Ketchup = 'KETCHUP',
  Screwball = 'SCREWBALL',
  TempTest = 'TEMP_TEST',
  Weller = 'WELLER',
}

export enum AchAccountType {
  Checking = 'CHECKING',
  Saving = 'SAVING',
}

export enum LiquidTreasuryDisclosureCategory {
  Bank = 'BANK',
  Brokerage = 'BROKERAGE',
  ESign = 'E_SIGN',
  General = 'GENERAL',
}

export enum LiquidTreasuryDisclosureType {
  AuthorizedRepresentativeCertification = 'AUTHORIZED_REPRESENTATIVE_CERTIFICATION',
  BankAccount = 'BANK_ACCOUNT',
  Brokerage = 'BROKERAGE',
  BusinessContinuityPlan = 'BUSINESS_CONTINUITY_PLAN',
  Card = 'CARD',
  Cashback = 'CASHBACK',
  ElectronicCommunications = 'ELECTRONIC_COMMUNICATIONS',
  ElectronicFundTransfers = 'ELECTRONIC_FUND_TRANSFERS',
  ESign = 'E_SIGN',
  FeeNotice = 'FEE_NOTICE',
  FinancialStatement = 'FINANCIAL_STATEMENT',
  FormCrs = 'FORM_CRS',
  Limitations = 'LIMITATIONS',
  PrivacyNotice = 'PRIVACY_NOTICE',
  PrivacyPolicy = 'PRIVACY_POLICY',
  TaxCertificationForUsPerson = 'TAX_CERTIFICATION_FOR_US_PERSON',
  TermsOfUse = 'TERMS_OF_USE',
  TruthInSavings = 'TRUTH_IN_SAVINGS',
  UsTreasuriesRisk = 'US_TREASURIES_RISK',
}

export interface LiquidTreasuryDisclosure {
  __typename?: 'LiquidTreasuryDisclosure'
  category: LiquidTreasuryDisclosureCategory
  title: string
  type: LiquidTreasuryDisclosureType
  url: string
}

export interface LiquidTreasuryDisclosureBundle {
  __typename?: 'LiquidTreasuryDisclosureBundle'
  accepted: boolean
  disclosures: LiquidTreasuryDisclosure[]
}

export enum Occupation {
  Education = 'EDUCATION',
  EngineeringSciences = 'ENGINEERING_SCIENCES',
  Finance = 'FINANCE',
  Healthcare = 'HEALTHCARE',
  Legal = 'LEGAL',
  MarketingAdvertising = 'MARKETING_ADVERTISING',
  Other = 'OTHER',
  ProfessionalServices = 'PROFESSIONAL_SERVICES',
  Sales = 'SALES',
  TechnologyIt = 'TECHNOLOGY_IT',
}

export enum AveragePersonalNetWorth {
  Below_50K = 'BELOW_50K',
  Between_1MAnd_5M = 'BETWEEN_1M_AND_5M',
  Between_50KAnd_200K = 'BETWEEN_50K_AND_200K',
  Between_200KAnd_1M = 'BETWEEN_200K_AND_1M',
  Over_5M = 'OVER_5M',
}

export enum PersonalNetWorthRange {
  Below_100K = 'BELOW_100K',
  Between_1MAnd_2M = 'BETWEEN_1M_AND_2M',
  Between_2MAnd_5M = 'BETWEEN_2M_AND_5M',
  Between_100KAnd_200K = 'BETWEEN_100K_AND_200K',
  Between_200KAnd_500K = 'BETWEEN_200K_AND_500K',
  Between_500KAnd_1M = 'BETWEEN_500K_AND_1M',
  Over_5M = 'OVER_5M',
}

export enum AppPlatform {
  Android = 'ANDROID',
  Ios = 'IOS',
  Web = 'WEB',
}

export enum AppType {
  Browser = 'BROWSER',
  Capacitor = 'CAPACITOR',
  Pwa = 'PWA',
}

export interface PlatformInfoOutput {
  __typename?: 'PlatformInfoOutput'
  appName?: string
  appPlatform?: AppPlatform
  appType?: AppType
  appVersion?: string
  isNativePlatform?: boolean
}
export interface EnrollmentRecommendationRangeOutput {
  __typename?: 'EnrollmentRecommendationRangeOutput'
  allowedMaxPercent: number
  allowedMinPercent: number
  recommendedPercent: number
}

export enum EquitiesSector {
  Consumer = 'CONSUMER',
  Energy = 'ENERGY',
  Financial = 'FINANCIAL',
  Healthcare = 'HEALTHCARE',
  Industry = 'INDUSTRY',
  Media = 'MEDIA',
  RealEstate = 'REAL_ESTATE',
  Technology = 'TECHNOLOGY',
}

export enum FixedIncomeCategory {
  CorporateBonds = 'CORPORATE_BONDS',
  GovernmentBonds = 'GOVERNMENT_BONDS',
  HighYieldBonds = 'HIGH_YIELD_BONDS',
  InternationalBonds = 'INTERNATIONAL_BONDS',
  MunicipalBonds = 'MUNICIPAL_BONDS',
}

export interface EnrollmentEquitySectorRecommendationOutput {
  __typename?: 'EnrollmentEquitySectorRecommendationOutput'
  maxAllocationPercent: number
  recommendedRange: EnrollmentRecommendationRangeOutput
  type: EquitiesSector
  currentAllocationPercent?: number
}

export enum CashEquivalentCategory {
  TripleTaxFree = 'TRIPLE_TAX_FREE',
  National = 'NATIONAL',
  TREASURY_BILL = 'TREASURY_BILL',
  Corporate = 'CORPORATE',
}

export interface EnrollmentCashEquivalentTypeRecommendationOutput {
  __typename?: 'EnrollmentCashEquivalentTypeRecommendationOutput'
  allowTripleTaxFreeMunicipalMutualFund: boolean
  recommended: CashEquivalentCategory
  current?: CashEquivalentCategory
}

export interface EnrollmentFixedIncomeRecommendationOutput {
  __typename?: 'EnrollmentFixedIncomeRecommendationOutput'
  maxAllocationPercent: number
  recommendedRange: EnrollmentRecommendationRangeOutput
  type: FixedIncomeCategory
  currentAllocationPercent?: number
}

export interface EnrollmentInvestmentRiskToleranceAllocationOutput {
  __typename?: 'EnrollmentInvestmentRiskToleranceAllocationOutput'
  cashEquivalentPercent: number
  equityPercent: number
  fixedIncomePercent: number
}

export interface EnrollmentInvestmentRiskToleranceOutput {
  __typename?: 'EnrollmentInvestmentRiskToleranceOutput'
  allocation: EnrollmentInvestmentRiskToleranceAllocationOutput
  category: InvestmentRiskToleranceCategory
  index: number
}

export interface EnrollmentRiskToleranceRecommendationOutput {
  __typename?: 'EnrollmentRiskToleranceRecommendationOutput'
  allowedRiskTolerances?: InvestmentRiskToleranceCategory
  recommendedRiskTolerance: InvestmentRiskToleranceCategory
  riskTolerances?: EnrollmentInvestmentRiskToleranceOutput
}

export interface EnrollmentInvestmentRecommendationOutput {
  __typename?: 'EnrollmentInvestmentRecommendationOutput'
  account?: EnrollmentInvestmentAccountRecommendationOutput
  cashEquivalentType?: EnrollmentCashEquivalentTypeRecommendationOutput
  equitySector?: EnrollmentEquitySectorRecommendationOutput
  fixedIncomeCategory?: EnrollmentFixedIncomeRecommendationOutput
  riskTolerance?: EnrollmentRiskToleranceRecommendationOutput
}

export interface EnrollmentRecommendationOutput {
  __typename?: 'EnrollmentRecommendationOutput'
  investment?: EnrollmentInvestmentRecommendationOutput
}

export interface EnrollmentUserInvestmentRiskToleranceOutput {
  __typename?: 'EnrollmentUserInvestmentRiskToleranceOutput'
  // current
  allocation: EnrollmentInvestmentRiskToleranceAllocationOutput
  type: InvestmentRiskToleranceCategory
  //recommended
  recommendedRiskTolerance?: InvestmentRiskToleranceCategory
  recommendedIndex?: number
  allowedRiskTolerances?: InvestmentRiskToleranceCategory[]
}

export interface EnrollmentInvestmentDisclosureOutputV2 {
  disclosureId: string
  disclosureUri: string
  disclosureName: string
}

export interface EnrollmentUserInvestmentInfoOutput {
  __typename?: 'EnrollmentUserInvestmentInfoOutput'
  isBrokerDealer?: boolean
  brokerDealerEmployer?: string
  isDirectorPolicyMakerOfCompany?: boolean
  directorPolicyMakerOfCompany?: string
  isPoliticallyExposedPerson?: boolean
  affiliatedPoliticalOrganizations?: string[]
  hasPoliticallyExposedImmediateFamilyMembers?: boolean
  politicallyExposedImmediateFamilyMembers?: string[]
  objective?: EnrollmentInvestmentObjectiveCategory
  experience?: EnrollmentInvestmentExperienceCategory

  riskTolerance?: EnrollmentUserInvestmentRiskToleranceOutput
  equitySectorAllocations?: EnrollmentEquitySectorRecommendationOutput[]
  fixedIncomeSectorAllocations?: EnrollmentFixedIncomeRecommendationOutput[]
  cashEquivalent?: EnrollmentCashEquivalentTypeRecommendationOutput
  disclosures: EnrollmentInvestmentDisclosureOutputV2[]

  canEnrollBrokerage?: boolean
  canEnrollTraditionalIra?: boolean
  canEnrollRothIra?: boolean
  canEnrollInheritedRothIra?: boolean
  canEnrollInheritedTraditionalIra?: boolean
}

export interface EnrollmentUserInfoOutput {
  __typename?: 'EnrollmentUserInfoOutput'
  employmentStatus?: EmploymentStatus
  income?: number
  investment?: EnrollmentUserInvestmentInfoOutput
  netWorthRange?: PersonalNetWorthRange
  occupation?: Occupation
}

export interface UserSourceDataOutput {
  __typename?: 'UserSourceDataOutput'
  completeJson?: string
  referrer?: string
  utmCampaign?: string
  utmContent?: string
  utmMedium?: string
  utmSource?: string
  utmTerm?: string
}

export interface EnrollmentDataOutput {
  __typename?: 'EnrollmentDataOutput'
  accountLinked?: boolean
  achAuthorization?: Disclosure
  addressCity?: string
  addressCountry?: string
  addressLine1?: string
  addressLine2?: string
  addressPostalCode?: string
  addressRegion?: string
  advWrapFeeDisclosure?: Disclosure
  advisoryPrivacyNotice?: Disclosure
  advisoryPrivacyPolicy?: Disclosure
  annualPersonalIncome?: AnnualPersonalIncome
  applicantId?: string
  applicationId?: number
  applicationStatus: ApplicationStatus
  bankName?: string
  brokerDealerAssociation?: string
  currentOccupation?: string
  customerRelationshipSummaryDisclosure?: Disclosure
  dateOfBirth?: string
  deviceInfo?: DeviceInfoOutput
  /** @deprecated Field no longer supported */
  directorPolicyMakerOfCompany?: string
  documentVerificationRequired?: boolean
  documentVerificationStatus?: DocumentVerificationStatus
  documentVerificationTriggered?: boolean
  documentVerificationUrl?: string
  eSessionId?: string
  eSignConsent?: Disclosure
  email?: string
  emailMagicLinkTriggered?: boolean
  emailMagicLinkVerified?: boolean
  emailOtpTriggered?: boolean
  emailOtpVerified?: boolean
  /** @deprecated Field no longer supported */
  employmentStatus?: EmploymentStatus
  experimentId?: EnrollmentExperimentId
  firstName?: string
  fullTermsAndConditions?: Disclosure
  fundingAccountName?: string
  fundingAchType?: AchAccountType
  investmentAdvisoryAgreementDisclosure?: Disclosure
  investmentProfile?: EnrollmentInvestmentProfileOutput
  isConditionalMediationAvailable?: boolean
  isMobile?: boolean
  isUserVerifyingPlatformAuthenticatorAvailable?: boolean
  lastName?: string
  liquidTreasuryDisclosureBundle?: LiquidTreasuryDisclosureBundle
  manageCash?: boolean
  maritalStatus?: MaritalStatus
  occupation?: Occupation
  pSessionId?: string
  passkeyPublicKeyCredentialRequestOptions?: string
  passkeyRegistered?: boolean
  passkeyTriggered?: boolean
  pecr?: string
  pect?: boolean
  /** @deprecated Field no longer supported */
  personalAnnualGrossIncome?: number
  personalNetWorth?: AveragePersonalNetWorth
  personalNetWorthRange?: PersonalNetWorthRange
  phoneCountryCode?: string
  phoneNumber?: string
  phoneOtpTriggered?: boolean
  phoneOtpVerified?: boolean
  plaidLinkToken?: string
  platformInfo?: PlatformInfoOutput
  privacyPolicy?: Disclosure
  rSessionId?: string
  recommendation?: EnrollmentRecommendationOutput
  riskCheckTriggered?: boolean
  standingLetterOfAuthorizationDisclosure?: Disclosure
  supportsPublicKeyCredential?: boolean
  tags?: string[]
  transferFundsInitiated?: boolean
  usCitizen?: boolean
  userId?: number
  userInfo?: EnrollmentUserInfoOutput
  userSource?: UserSourceDataOutput
}

export enum EnrollmentAccountType {
  BROKERAGE = 'BROKERAGE',
  ROTH_IRA = 'ROTH_IRA',
  TRADITIONAL_IRA = 'TRADITIONAL_IRA',
}
